import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Plus, Minus, Check } from 'lucide-react';
import type { Supplement } from '../types';
import { useDialog } from '../hooks/useDialog';

interface Props {
  item: { id: string; title: string; price: number };
  supplements: Supplement[];
  onClose: () => void;
  onConfirm: (selected: Supplement[]) => void;
}

export function SupplementPickerModal({ item, supplements, onClose, onConfirm }: Props) {
  const dialogRef = useDialog(true, onClose);
  // selected[id] = quantity (>=1)
  const [selected, setSelected] = useState<Record<string, number>>({});

  const toggle = (supp: Supplement) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[supp.id] != null) {
        delete next[supp.id];
      } else {
        next[supp.id] = 1;
      }
      return next;
    });
  };

  const setQty = (supp: Supplement, qty: number) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (qty <= 0) {
        delete next[supp.id];
      } else {
        next[supp.id] = qty;
      }
      return next;
    });
  };

  const selectedList = supplements.filter((s) => selected[s.id] != null);
  const supplementsTotal = selectedList.reduce(
    (sum, s) => sum + s.price * (selected[s.id] ?? 1),
    0,
  );
  const lineTotal = item.price + supplementsTotal;

  const handleConfirm = () => {
    const result: Supplement[] = selectedList.map((s) => ({
      ...s,
      quantity: selected[s.id] ?? 1,
    }));
    onConfirm(result);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Добавки към ${item.title}`}
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl flex flex-col max-h-[85vh] shadow-2xl"
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div className="pr-3">
              <h3 className="font-heading font-bold text-lg text-gusto-dark leading-tight">
                {item.title}
              </h3>
              <p className="text-xs text-gusto-gray mt-0.5">
                Избери добавки по желание
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 shrink-0 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gusto-dark" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-2">
            {supplements.map((supp) => {
              const qty = selected[supp.id];
              const isActive = qty != null;
              return (
                <div
                  key={supp.id}
                  className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${
                    isActive
                      ? 'border-gusto-red bg-gusto-red/5'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <button
                    onClick={() => toggle(supp)}
                    className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                      isActive
                        ? 'bg-gusto-red text-white'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    aria-label={isActive ? 'Премахни добавка' : 'Добави добавка'}
                  >
                    <Check className="w-5 h-5" />
                  </button>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gusto-dark truncate">
                      {supp.name}
                    </p>
                    <p className="text-xs text-gusto-gray">
                      +{supp.price.toFixed(2)} €
                    </p>
                  </div>

                  {isActive && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setQty(supp, qty - 1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-gusto-red hover:text-gusto-red transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-medium">
                        {qty}
                      </span>
                      <button
                        onClick={() => setQty(supp, qty + 1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-gusto-red hover:text-gusto-red transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-5 border-t border-gray-100">
            <button
              onClick={handleConfirm}
              className="w-full flex items-center justify-between bg-gusto-red hover:bg-gusto-red/90 text-white font-semibold px-5 py-3.5 rounded-2xl transition-colors"
            >
              <span className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Добави в количката
              </span>
              <span>{lineTotal.toFixed(2)} €</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
